  import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
  import { Injectable, inject } from '@angular/core';
  import { Observable, catchError, throwError } from 'rxjs';
  import { ErrorHandleService } from '../services/error-handle.service';
  import { SentryService } from '../services/sentry/sentry.service';
  
  type HttpErrorConfig = {
	type: 'error';
	title: string;
	subtitle?: string;
	message?: string;
	button?: string;
	icon?: string;
	error_code?: string;
  };
  
  type ErrorConfigMap = {
	[key: number]: HttpErrorConfig;
	default: HttpErrorConfig;
  };
  
@Injectable()
export class ErrorHandlingInterceptor implements HttpInterceptor {

	private errorConfig: ErrorConfigMap = {
	400: {
		type: 'error',
		icon: 'assets/icons/ic_error.svg',
		title: 'MODAL_LABELS.TECHNICAL_ERROR',
		subtitle: 'MODAL_LABELS.ERROR_400',
		button: 'BUTTONS_LABELS.ACCEPT',
	},
	401: {
		type: 'error',
		icon: 'assets/icons/ic_error.svg',
		title: 'MODAL_LABELS.TECHNICAL_ERROR',
		subtitle: 'MODAL_LABELS.ERROR_401',
		button: 'BUTTONS_LABELS.ACCEPT',
	},
	403: {
		type: 'error',
		icon: 'assets/icons/ic_error.svg',
		title: 'MODAL_LABELS.TECHNICAL_ERROR',
		subtitle: 'MODAL_LABELS.ERROR_403',
		button: 'BUTTONS_LABELS.ACCEPT',
	},
	404: {
		type: 'error',
		icon: 'assets/icons/ic_error.svg',
		title: 'MODAL_LABELS.TECHNICAL_ERROR',
		subtitle: 'MODAL_LABELS.ERROR_400', // Usando el mismo mensaje que 400
		button: 'BUTTONS_LABELS.ACCEPT',
	},
	500: {
		type: 'error',
		icon: 'assets/icons/ic_error.svg',
		title: 'MODAL_LABELS.TECHNICAL_ERROR',
		subtitle: 'MODAL_LABELS.TECHNICAL_ERROR_DESC',
		button: 'BUTTONS_LABELS.ACCEPT',
	},
	default: {
		type: 'error',
		icon: 'assets/icons/ic_error.svg',
		title: 'MODAL_LABELS.TECHNICAL_ERROR',
		subtitle: 'MODAL_LABELS.TECHNICAL_ERROR_DESC',
		button: 'BUTTONS_LABELS.ACCEPT',
	}
};

private _sentrySrv = inject(SentryService);

constructor(public errorHandle: ErrorHandleService) {}

	intercept(
		request: HttpRequest<unknown>, 
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		return next.handle(request).pipe(
		catchError((err: HttpErrorResponse) => {
			if (err) {
				this.errorHandle.show(this.buildErrorConfig(err));
				this.captureUnexpectedError(err, request);
			}

			return throwError(() => err);
		})
		);
	}

	private buildErrorConfig(err: HttpErrorResponse) {
		const errorStatus = err.status as keyof typeof this.errorConfig;
		const config = { 
			...(this.errorConfig[errorStatus] || this.errorConfig.default) 
		};

		// Si hay un mensaje específico del backend, lo usamos
		if (err.error?.message) {
			config.subtitle = err.error.message;
		}

		// Si hay un código de error específico
		if (err.error?.code) {
			config.error_code = err.error.code;
		}

		// Si hay información adicional
		if (err.error?.additionalInfo) {
			config.message = err.error.additionalInfo;
		}

		// Si es un error de validación específico
		if (err.error?.type === 'validation') {
			config.title = 'MODAL_LABELS.VALIDATION_ERROR';
			config.subtitle = 'MODAL_LABELS.VALIDATION_ERROR_DESC';
		}

		// Si es un error de OTP
		if (err.error?.type === 'otp') {
			config.title = 'MODAL_LABELS.TECHNICAL_ERROR';
			config.subtitle = 'MODAL_LABELS.INVALID_OTP_DESC';
		}

		return config;
	}

	private captureUnexpectedError(err: HttpErrorResponse, request: HttpRequest<unknown>): void {
		// No capturar errores esperados del backend en Sentry (comportamiento normal del negocio)
		const isExpected404NotFound = err.status === 404 && (
			request.url.includes('/Client/hybrid-security/account') || // Documento no existe
			request.url.includes('/Client/address') || // Dirección no encontrada
			request.url.includes('/Client/finance-info') // Info financiera no existe
		);

		// Caída de conexión momentánea (sin respuesta del servidor): no es un error de la app
		const isExpectedConnectionFailure = err.status === 0;

		// Código de verificación (OTP) inválido o expirado: ya se le explica al usuario en pantalla
		const isExpectedOtpError = err.error?.type === 'otp';

		// Capturar error HTTP en Sentry con contexto completo (excepto errores esperados)
		if (!isExpected404NotFound && !isExpectedConnectionFailure && !isExpectedOtpError) {
			this._sentrySrv.captureException(err, {
				httpRequest: {
					url: request.url,
					method: request.method,
					statusCode: err.status,
					statusText: err.statusText,
				},
				httpResponse: {
					body: err.error,
					headers: err.headers,
				},
				additionalContext: {
					errorCode: err.error?.code,
					errorType: err.error?.type,
					message: err.error?.message,
					additionalInfo: err.error?.additionalInfo,
				}
			});
		}
	}
}